// src/components/Header.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Link, NavLink, useLocation } from "react-router-dom";
import { FaBars, FaTimes, FaChevronDown, FaUserShield } from "react-icons/fa";

import { auth } from "../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";

/**
 * Props:
 *  - logoSrc?: string
 *  - clubName?: string (default "Hofors BGK")
 */
const Header = ({ logoSrc, clubName = "Hofors BGK" }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // desktop dropdown id
  const [mobileOpenGroup, setMobileOpenGroup] = useState(null); // mobile accordion group
  const [user, setUser] = useState(null);

  const location = useLocation();
  const headerRef = useRef(null);

  // Firebase auth-state
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return () => unsub();
  }, []);

  // Lås scroll när mobilmenyn är öppen
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [menuOpen]);

  // Stäng mobilmeny + dropdown vid route/hash-change
  useEffect(() => {
    setMenuOpen(false);
    setOpenDropdown(null);
    setMobileOpenGroup(null);
  }, [location.pathname, location.hash]);

  // ESC stänger mobilmeny + dropdown
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        setOpenDropdown(null);
        setMobileOpenGroup(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Klick utanför stänger dropdown (desktop)
  useEffect(() => {
    const onDocClick = (e) => {
      if (!openDropdown) return;
      const el = headerRef.current;
      if (el && !el.contains(e.target)) setOpenDropdown(null);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [openDropdown]);

  const closeAll = () => {
    setMenuOpen(false);
    setOpenDropdown(null);
    setMobileOpenGroup(null);
  };

  const toggleMobileGroup = (id) => {
    setMobileOpenGroup((prev) => (prev === id ? null : id));
  };

  const handleLogout = async () => {
    await signOut(auth);
    closeAll();
  };

  // Meny-grupper (submenyer)
  const groups = useMemo(() => {
    const g = [
      {
        id: "club",
        label: "Klubben",
        items: [
          { to: "/foreningen", label: "Föreningen" },
          { to: "/avgifter", label: "Avgifter" },
          { to: "/regler", label: "Regler" },
          { to: "/sponsors", label: "Sponsorer" },
          { to: "/kontakt", label: "Kontakt", hash: true },
        ],
      },
      {
        id: "play",
        label: "Spela",
        items: [
          { to: "/opening-hours", label: "Öppettider" },
          { to: "/banor", label: "Banor" },
          { to: "/tavlingar", label: "Tävlingar" },
        ],
      },
      {
        id: "media",
        label: "Nyheter & Media",
        items: [
          { to: "/news", label: "Nyheter" },
          { to: "/galleri", label: "Galleri" },
        ],
      },
      { id: "shop", label: "Butik", items: [{ to: "/butik", label: "Produkter" }] },
    ];

    // ✅ Admin-menyn baserat på Firebase user
    if (user) {
      g.push({
        id: "admin",
        label: "Admin",
        admin: true,
        items: [
          { to: "/admin", label: "Adminpanel", admin: true },
          { action: "logout", label: "Logga ut", admin: true },
        ],
      });
    } else {
      g.push({
        id: "admin",
        label: "Admin",
        admin: true,
        items: [{ to: "/admin/login", label: "Logga in", admin: true }],
      });
    }

    return g;
  }, [user]);

  return (
    <>
      <GlobalUX />

      <Wrap ref={headerRef}>
        <Accent />
        <Inner>
          <Brand to="/" onClick={closeAll} aria-label={clubName} title={clubName}>
            {logoSrc ? (
              <LogoImg src={logoSrc} alt={clubName} />
            ) : (
              <LogoText>
                Hofors <span>BGK</span>
              </LogoText>
            )}
          </Brand>

          {/* Desktop menu */}
          <DesktopNav aria-label="Huvudmeny">
            <TopLink to="/" end>
              Hem
            </TopLink>

            {groups.map((g) => {
              const open = openDropdown === g.id;
              const isMega = (g.items?.length || 0) >= 5;

              return (
                <Drop key={g.id}>
                  <DropBtn
                    type="button"
                    $admin={!!g.admin}
                    aria-expanded={open}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenDropdown(open ? null : g.id);
                    }}
                  >
                    {g.admin && <FaUserShield style={{ marginRight: 8 }} />}
                    {g.label}
                    <Chevron $open={open}>
                      <FaChevronDown />
                    </Chevron>
                  </DropBtn>

                  {open && (
                    <DropMenu
                      $mega={isMega}
                      onClick={(e) => e.stopPropagation()}
                      role="menu"
                      aria-label={g.label}
                    >
                      {g.items.map((it) =>
                        it.action === "logout" ? (
                          <DropLink
                            as="button"
                            type="button"
                            key="logout"
                            role="menuitem"
                            onClick={handleLogout}
                            $admin
                          >
                            <FaUserShield style={{ marginRight: 8 }} />
                            Logga ut
                          </DropLink>
                        ) : it.hash ? (
                          <DropLink
                            key={it.to}
                            as={Link}
                            to={it.to}
                            role="menuitem"
                            onClick={() => setOpenDropdown(null)}
                          >
                            {it.label}
                          </DropLink>
                        ) : (
                          <DropLink
                            key={it.to}
                            to={it.to}
                            role="menuitem"
                            onClick={() => setOpenDropdown(null)}
                            $admin={!!it.admin}
                          >
                            {it.admin && <FaUserShield style={{ marginRight: 8 }} />}
                            {it.label}
                          </DropLink>
                        )
                      )}
                    </DropMenu>
                  )}
                </Drop>
              );
            })}
          </DesktopNav>

          {/* Right */}
          <Right>
            <Burger type="button" onClick={() => setMenuOpen(true)} aria-label="Öppna meny" title="Meny">
              <FaBars />
            </Burger>
          </Right>
        </Inner>
      </Wrap>

      {/* Mobile menu */}
      {menuOpen && (
        <MobileOverlay
          role="dialog"
          aria-modal="true"
          aria-label="Meny"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeAll();
          }}
        >
          <MobilePanel onMouseDown={(e) => e.stopPropagation()}>
            <MobileTop>
              <Brand to="/" onClick={closeAll} aria-label={clubName}>
                {logoSrc ? (
                  <LogoImg src={logoSrc} alt={clubName} />
                ) : (
                  <LogoText>
                    Hofors <span>BGK</span>
                  </LogoText>
                )}
              </Brand>

              <Burger type="button" onClick={closeAll} aria-label="Stäng meny" title="Stäng">
                <FaTimes />
              </Burger>
            </MobileTop>

            <MobileNav>
              <MobileItem to="/" end onClick={closeAll}>
                Hem
              </MobileItem>

              {groups.map((g) => {
                const open = mobileOpenGroup === g.id;

                return (
                  <MobileGroup key={g.id}>
                    <MobileGroupBtn
                      type="button"
                      onClick={() => toggleMobileGroup(g.id)}
                      aria-expanded={open}
                      $admin={!!g.admin}
                    >
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        {g.admin && <FaUserShield />}
                        {g.label}
                      </div>
                      <Chevron $open={open}>
                        <FaChevronDown />
                      </Chevron>
                    </MobileGroupBtn>

                    {open && (
                      <MobileGroupList>
                        {g.items.map((it) =>
                          it.action === "logout" ? (
                            <MobileSubItem
                              as="button"
                              type="button"
                              key="logout"
                              onClick={handleLogout}
                              $admin
                            >
                              <FaUserShield style={{ marginRight: 8 }} />
                              Logga ut
                            </MobileSubItem>
                          ) : it.hash ? (
                            <MobileSubItem key={it.to} as={Link} to={it.to} onClick={closeAll}>
                              {it.label}
                            </MobileSubItem>
                          ) : (
                            <MobileSubItem key={it.to} to={it.to} onClick={closeAll} $admin={!!it.admin}>
                              {it.admin && <FaUserShield style={{ marginRight: 8 }} />}
                              {it.label}
                            </MobileSubItem>
                          )
                        )}
                      </MobileGroupList>
                    )}
                  </MobileGroup>
                );
              })}
            </MobileNav>

            <MobileHint>Tips: Klicka på en rubrik för att öppna/stäng 👆</MobileHint>
          </MobilePanel>
        </MobileOverlay>
      )}
    </>
  );
};

export default Header;

/* ------------------ Global UX ------------------ */

const GlobalUX = () => (
  <style>{`
    html { scroll-behavior: smooth; }
  `}</style>
);

/* ------------------ Styling ------------------ */

const colors = {
  text: "#111827",
  muted: "#4b5563",
  border: "#e5e7eb",
  card: "#ffffff",
  shadow: "0 12px 34px rgba(0, 0, 0, 0.06)",
  shadow2: "0 24px 80px rgba(0, 0, 0, 0.12)",
  accent: "#0f766e",
  accent2: "#e94e1b",
  chip: "rgba(15,118,110,0.12)",
};

const Wrap = styled.header`
  position: sticky;
  top: 0;
  z-index: 1200;
  background: rgba(255, 255, 255, 0.84);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid ${colors.border};
`;

const Accent = styled.div`
  height: 3px;
  background: linear-gradient(90deg, ${colors.accent}, ${colors.accent2});
`;

const Inner = styled.div`
  max-width: 1480px;
  margin: 0 auto;
  padding: 12px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
`;

const Brand = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
`;

const LogoImg = styled.img`
  height: 46px;
  width: auto;
  display: block;

  @media (min-width: 900px) {
    height: 58px;
  }
`;

const LogoText = styled.div`
  font-size: 20px;
  font-weight: 950;
  color: ${colors.text};
  letter-spacing: 0.4px;

  span {
    color: ${colors.accent};
  }
`;

const DesktopNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 1080px) {
    display: none;
  }
`;

const TopLink = styled(NavLink)`
  text-decoration: none;
  font-weight: 950;
  color: ${colors.muted};
  padding: 9px 12px;
  border-radius: 999px;
  border: 1px solid transparent;
  transition: transform 140ms ease, background 140ms ease, color 140ms ease;

  &.active {
    background: ${colors.chip};
    color: ${colors.accent};
    border-color: rgba(15,118,110,0.18);
  }

  &:hover {
    background: rgba(0,0,0,0.04);
    transform: translateY(-1px);
  }
`;

const Drop = styled.div`
  position: relative;
`;

const DropBtn = styled.button`
  appearance: none;
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;

  display: inline-flex;
  align-items: center;
  gap: 8px;

  font-weight: 950;
  color: ${colors.muted};
  padding: 9px 12px;
  border-radius: 999px;

  transition: transform 140ms ease, background 140ms ease, border-color 140ms ease;

  &:hover {
    background: rgba(0,0,0,0.04);
    transform: translateY(-1px);
  }

  ${({ $admin }) =>
    $admin
      ? `
        color: ${colors.text};
        border-color: ${colors.border};
        background: rgba(0,0,0,0.02);
      `
      : ""}
`;

const Chevron = styled.span`
  display: inline-flex;
  transition: transform 140ms ease;
  transform: rotate(${(p) => (p.$open ? "180deg" : "0deg")});
`;

const DropMenu = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  left: 0;

  min-width: 280px;
  padding: 10px;
  border-radius: 18px;

  background: ${colors.card};
  border: 1px solid ${colors.border};
  box-shadow: ${colors.shadow2};

  display: grid;
  gap: 8px;

  grid-template-columns: ${(p) => (p.$mega ? "repeat(2, minmax(0, 1fr))" : "1fr")};
`;

const DropLink = styled(NavLink)`
  text-decoration: none;
  color: ${colors.text};
  font-weight: 900;

  padding: 10px 12px;
  border-radius: 14px;
  border: 1px solid transparent;
  background: transparent;

  transition: transform 140ms ease, background 140ms ease;

  &:hover {
    background: rgba(0,0,0,0.04);
    transform: translateY(-1px);
  }

  &.active {
    background: ${colors.chip};
    border-color: rgba(15,118,110,0.18);
    color: ${colors.accent};
  }

  ${({ $admin }) =>
    $admin
      ? `
        border-color: ${colors.border};
        background: rgba(0,0,0,0.02);
      `
      : ""}

  /* ✅ så det funkar när den renderas som button */
  &[type="button"] {
    text-align: left;
    width: 100%;
    cursor: pointer;
  }
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Burger = styled.button`
  appearance: none;
  border: 1px solid ${colors.border};
  background: ${colors.card};
  color: ${colors.text};
  border-radius: 14px;
  padding: 10px 11px;
  cursor: pointer;
  box-shadow: ${colors.shadow};

  display: inline-flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 1081px) {
    display: none;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${colors.shadow2};
  }
`;

/* Mobile */

const MobileOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2500;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(12px);
  padding: 16px;
`;

const MobilePanel = styled.div`
  max-width: 820px;
  margin: 0 auto;
  border-radius: 22px;
  border: 1px solid ${colors.border};
  background: ${colors.card};
  box-shadow: ${colors.shadow2};
  padding: 14px;

  max-height: calc(100vh - 32px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const MobileTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MobileNav = styled.nav`
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MobileItem = styled(NavLink)`
  text-decoration: none;
  font-size: 18px;
  font-weight: 950;
  color: ${colors.text};

  padding: 14px 14px;
  border-radius: 18px;
  border: 1px solid ${colors.border};
  background: ${colors.card};

  &.active {
    border-color: rgba(15,118,110,0.28);
    background: ${colors.chip};
    color: ${colors.accent};
  }
`;

const MobileGroup = styled.div`
  border: 1px solid ${colors.border};
  border-radius: 18px;
  background: ${colors.card};
  overflow: hidden;
`;

const MobileGroupBtn = styled.button`
  appearance: none;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;

  padding: 12px 14px;
  font-weight: 950;
  color: ${colors.text};
  background: rgba(0, 0, 0, 0.02);

  display: flex;
  align-items: center;
  justify-content: space-between;

  border-bottom: 1px solid ${colors.border};

  ${({ $admin }) =>
    $admin
      ? `
        color: ${colors.accent};
      `
      : ""}
`;

const MobileGroupList = styled.div`
  padding: 10px;
  display: grid;
  gap: 8px;
`;

const MobileSubItem = styled(NavLink)`
  text-decoration: none;
  font-size: 16px;
  font-weight: 900;
  color: ${colors.text};

  padding: 12px 12px;
  border-radius: 14px;
  border: 1px solid transparent;
  background: transparent;

  &:hover {
    background: rgba(0,0,0,0.04);
  }

  &.active {
    background: ${colors.chip};
    border-color: rgba(15,118,110,0.18);
    color: ${colors.accent};
  }

  ${({ $admin }) =>
    $admin
      ? `
        border-color: ${colors.border};
        background: rgba(0,0,0,0.02);
      `
      : ""}

  /* ✅ så det funkar när den renderas som button */
  &[type="button"] {
    text-align: left;
    width: 100%;
    cursor: pointer;
  }
`;

const MobileHint = styled.div`
  margin-top: 12px;
  text-align: center;
  color: ${colors.muted};
  font-weight: 900;
  font-size: 12px;
`;

